import { generatePathDatacentral, paths } from '@/utils/path'
import type { TFunction } from 'i18next'
import type { BadgeTone } from '@vnetwork/datacentral-ui/primitives'
import {
    CONTRACT_STATUS_CONFIG,
    ContractStatus,
    CUSTOMER_STATUS_CONFIG,
    CustomerStatus,
    INVOICE_STATUS_CONFIG,
    InvoiceStatus,
    SearchResult,
    SearchResultWrapLine,
    SERVICE_STATUS_CONFIG,
    ServiceStatus,
} from '../types'
import { Receipt, Users, Server } from 'lucide-react'

const status = (t: TFunction, config?: { label: string; tone: BadgeTone }) =>
    config ? { label: t(config.label), tone: config.tone } : undefined

/** Display line (label, subtitle, link, status, icon) for one global-search hit. */
export function wrapSearchResult(
    rs: SearchResult,
    t: TFunction
): SearchResultWrapLine {
    const base = { created_at: rs.created_at, email: rs.email }
    switch (rs.type) {
        case 'customer':
            return {
                ...base,
                label: [rs.customer_id, rs.company_name].join(' - '),
                redirect_to: generatePathDatacentral(paths.customerDetail, {
                    id: rs.customer_id,
                }),
                status: status(
                    t,
                    CUSTOMER_STATUS_CONFIG[rs.customer_status as CustomerStatus]
                ),
                icon: <Users className="w-4 h-4" />,
            }
        case 'service':
            return {
                ...base,
                label: [
                    rs.service_id,
                    rs.service_name,
                    `(${rs.entity_name})`,
                ].join(' - '),
                subtitle: [rs.customer_id, rs.company_name].join(' - '),
                redirect_to: generatePathDatacentral(paths.serviceDetail, {
                    id: rs.service_id,
                }),
                status: status(
                    t,
                    SERVICE_STATUS_CONFIG[rs.service_status as ServiceStatus]
                ),
                icon: <Server className="w-4 h-4" />,
            }
        case 'contract':
            return {
                ...base,
                label: [
                    rs.customer_id,
                    rs.company_name,
                    rs.contract_number_id,
                ].join(' - '),
                redirect_to: generatePathDatacentral(
                    paths.customerContractDetail,
                    { id: rs.contract_id }
                ),
                status: status(
                    t,
                    CONTRACT_STATUS_CONFIG[rs.contract_status as ContractStatus]
                ),
                icon: <Receipt className="w-4 h-4" />,
            }
        case 'invoice':
            return {
                ...base,
                label: [rs.customer_id, rs.company_name, rs.invoice_code].join(
                    ' - '
                ),
                redirect_to: generatePathDatacentral(paths.invoiceDetail, {
                    id: rs.invoice_code,
                }),
                status: status(
                    t,
                    rs.invoice_status
                        ? INVOICE_STATUS_CONFIG[
                              rs.invoice_status as InvoiceStatus
                          ]
                        : undefined
                ),
                icon: <Receipt className="w-4 h-4" />,
            }
        default:
            return { ...base, label: rs.type }
    }
}
